import { cloneDeep } from "lodash";
import { nanoid } from "nanoid";
import ILessonPageDto from "../models/ILessonPageDto";
import { LanguageType } from "../models/IInteractiveAudio";

/** Audio state reset applied to copied narrations/effects */
const INITIAL_AUDIO_STATE = {
  ready: false,
  blob: undefined,
  playing: false,
  loading: false,
} as const;

export interface CopyPageInput {
  /** The source page being copied */
  sourcePage: ILessonPageDto;
  /** The order index for the new page */
  pageOrder: number;
  /** The lesson version ID */
  lessonVersionId: number;
  /** The new page version ID from the ClonePage API */
  newPageVersionId: number;
  /** The asset version IDs returned from ClonePage API */
  newAssetVersionIds: number[];
  /** Live objects state from the editor */
  objectsState: {
    images: any[];
    textBoxes: any[];
    annotations: any[];
    animatedObjects: any[];
    videos: any[];
    tables: any[];
    objectList: any[];
    hotspots: any[];
    smartObjects: any[];
    panoramicList: any[];
    shapeList: any[];
  };
  /** Live audio state from the audio manager */
  audioState: {
    narrationAudios: any[];
    audioEffects: any[];
  };
  /** Meta variables from the meta variable store */
  metaVariables: {
    metaVariables: any;
    metaVariablesData: any;
    animatedMetaVariables: any[];
  };
  /** Total lesson pages count (for uniquePageListerID) */
  totalPagesCount: number;
}

export interface CopyPageResult {
  /** The new page object ready to be added to in-memory state */
  newPage: ILessonPageDto;
  /**
   * Whether the result should be persisted immediately via updateLesson.
   * Must always be false — page should only be saved when the user saves.
   */
  shouldSaveImmediately: boolean;
}

/**
 * Builds a copied page from a source page and live editor state.
 *
 * This is a pure function that returns a new page object. It does NOT
 * trigger any API calls or state mutations. The caller is responsible for
 * dispatching the result into state and letting the normal save flow persist it.
 */
export function buildCopiedPage(input: CopyPageInput): CopyPageResult {
  const {
    sourcePage,
    pageOrder,
    lessonVersionId,
    newPageVersionId,
    newAssetVersionIds,
    objectsState,
    audioState,
    metaVariables,
    totalPagesCount,
  } = input;

  const sourceManifest = sourcePage.pageManifest;

  // Build new page manifest starting from the source, overlaying live editor state
  const newManifest: any = {
    ...cloneDeep(sourceManifest),
    pageImage: objectsState.images,
    annotations: objectsState.annotations,
    textBlock: objectsState.textBoxes,
    replacedAssets: sourceManifest.replacedAssets || [],
  };

  // Timeline: copy from source if exists, overlay animated objects from live state
  if (sourceManifest.timeline) {
    newManifest.timeline = {
      ...cloneDeep(sourceManifest.timeline),
      animatedObjects: objectsState.animatedObjects,
    };
  } else if (objectsState.animatedObjects.length > 0) {
    newManifest.timeline = {
      animatedObjects: objectsState.animatedObjects,
      sequenceLength: 30,
    };
  }

  // Interactivity — always copy from source manifest
  if (sourceManifest.interactivity) {
    newManifest.interactivity = cloneDeep(sourceManifest.interactivity);
  }

  // Meta variables
  if (metaVariables.metaVariables) {
    newManifest.metaVariables = metaVariables.metaVariables;
  }
  if (metaVariables.metaVariablesData) {
    newManifest.metaVariablesData = metaVariables.metaVariablesData;
  }
  if (metaVariables.animatedMetaVariables) {
    newManifest.animatedMetaVariables = metaVariables.animatedMetaVariables;
  }

  // SCORM
  const scorm = objectsState.objectList?.find((object) => object?.type === "SCORM");
  if (scorm) {
    newManifest.pageScormObject = [scorm];
  }

  // Video
  if (objectsState.videos[0]) {
    newManifest.video = objectsState.videos[0];
  }

  // Tables
  if (objectsState.tables) {
    newManifest.tables = objectsState.tables;
  }

  // Hotspots
  if (objectsState.hotspots) {
    newManifest.hotspots = objectsState.hotspots;
  }

  // Panoramic
  if (objectsState.panoramicList) {
    newManifest.panoramicList = objectsState.panoramicList;
  }

  // Shapes
  if (objectsState.shapeList) {
    newManifest.shapeList = objectsState.shapeList;
  }

  // Smart objects
  if (objectsState.smartObjects) {
    newManifest.smartObjects = objectsState.smartObjects;
  }

  // Narrations — copy with new IDs and reset audio state
  if (audioState.narrationAudios && audioState.narrationAudios.length > 0) {
    const primaryIdRemap = new Map<string, string>();
    newManifest.narrationAudios = audioState.narrationAudios
      .map((audio: any) => {
        const newId = nanoid();
        if (audio.language === LanguageType.PRIMARY) {
          primaryIdRemap.set(audio.objectId, newId);
        }
        return { ...audio, ...INITIAL_AUDIO_STATE, objectId: newId };
      })
      .map((audio: any) => {
        if (audio.parentObjectId) {
          audio.parentObjectId = primaryIdRemap.get(audio.parentObjectId);
        }
        return audio;
      });
  }

  // Audio effects — copy with new IDs and reset audio state
  if (audioState.audioEffects && audioState.audioEffects.length > 0) {
    newManifest.audioEffects = audioState.audioEffects.map((audio: any) => ({
      ...audio,
      ...INITIAL_AUDIO_STATE,
      objectId: nanoid(),
    }));
  }

  const newPage: ILessonPageDto = {
    name: `${sourcePage.name} Copy`,
    order: pageOrder,
    blobPath: "",
    shouldAutoPageForward: sourcePage.shouldAutoPageForward,
    isScorable: sourcePage.isScorable,
    lessonVersionId,
    lessonPageId: -1,
    pageId: -1,
    pageManifest: newManifest,
    pagePlayerType: sourcePage.pagePlayerType,
    pageVersionId: newPageVersionId,
    pageAssetIds: newAssetVersionIds,
    isClientSideNewPage: true,
    lessonPageIsDirty: false,
    pageManifestIsDirty: true,
    lessonModeIds: sourcePage.lessonModeIds,
    uniquePageListerID: totalPagesCount - 7,
    referenceLessonId: sourcePage.referenceLessonId,
    referenceLessonVersionId: sourcePage.referenceLessonVersionId,
  };

  return {
    newPage,
    shouldSaveImmediately: false,
  };
}
